import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Pencil, Check, X, Trash2 } from "lucide-react";

const DOT_COLORS = ["#D97757", "#2D9E68", "#2E7CF6", "#E0A63C", "#8B5CF6", "#E0475F", "#0EA5A4"];

export const DenominationsPanel = ({ canManage = true }) => {
  const { lang, setConfig } = useApp();
  const [items, setItems] = useState([]);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null); // {id, name}
  const [titles, setTitles] = useState(null);
  const [newTitle, setNewTitle] = useState("");

  const load = () => api.get("/admin/denominations").then(({ data }) => setItems(data.denominations || [])).catch(() => {});
  useEffect(() => {
    load();
    api.get("/admin/settings").then(({ data }) => setTitles(data.settings?.leader_titles || [])).catch(() => setTitles([]));
  }, []);

  const refreshConfig = async () => {
    try { const c = await api.get(`/config/public?lang=${lang}`); setConfig(c.data); } catch { /* noop */ }
  };

  const add = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await api.post("/admin/denominations", { name });
      setNewName("");
      toast.success("Obédience ajoutée");
      load(); refreshConfig();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };

  const patch = async (id, changes) => {
    try { await api.patch(`/admin/denominations/${id}`, changes); load(); refreshConfig(); }
    catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };

  const saveRename = async () => {
    if (!editing?.name.trim()) return;
    await patch(editing.id, { name: editing.name.trim() });
    toast.success("Obédience renommée (membres et églises mis à jour)");
    setEditing(null);
  };

  const move = async (d, delta) => {
    const sorted = [...items].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((x) => x.id === d.id);
    const target = sorted[idx + delta];
    if (!target) return;
    try {
      await api.patch(`/admin/denominations/${d.id}`, { order: target.order });
      await api.patch(`/admin/denominations/${target.id}`, { order: d.order });
      load(); refreshConfig();
    } catch { toast.error("Erreur"); }
  };

  const removeD = async (d) => {
    if (!window.confirm(`Supprimer l'obédience « ${d.name} » ? Les membres qui l'ont choisie la garderont dans leur profil.`)) return;
    try { await api.delete(`/admin/denominations/${d.id}`); toast.success("Obédience supprimée"); load(); refreshConfig(); }
    catch { toast.error("Erreur"); }
  };

  const approve = async (d) => {
    await patch(d.id, { active: true, status: "approved" });
    toast.success(`« ${d.name} » approuvée et ajoutée à la liste`);
  };

  const reject = async (d) => {
    if (!window.confirm(`Rejeter l'obédience proposée « ${d.name} » ?`)) return;
    try { await api.delete(`/admin/denominations/${d.id}`); toast.success("Proposition rejetée"); load(); refreshConfig(); }
    catch { toast.error("Erreur"); }
  };

  const addTitle = async () => {
    const t = newTitle.trim();
    if (!t || titles.includes(t)) return;
    const next = [...titles, t];
    setTitles(next); setNewTitle("");
    try { await api.patch("/admin/settings", { leader_titles: next }); refreshConfig(); toast.success("Titre ajouté"); }
    catch { toast.error("Erreur"); }
  };

  const removeTitle = async (t) => {
    const next = titles.filter((x) => x !== t);
    setTitles(next);
    try { await api.patch("/admin/settings", { leader_titles: next }); refreshConfig(); }
    catch { toast.error("Erreur"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-denominations-panel">
      {(() => {
        const pending = items.filter((d) => d.status === "pending");
        if (pending.length === 0) return null;
        return (
          <div className="yalola-card p-4" style={{ borderColor: "var(--yalola-primary)", borderWidth: 1 }} data-testid="denom-pending-section">
            <div className="font-bold font-display flex items-center gap-2">
              <span className="inline-flex h-2 w-2 rounded-full" style={{ background: "var(--yalola-primary)" }} />
              Anciennes propositions de membres — à trier ({pending.length})
            </div>
            <p className="text-xs mb-3" style={{ color: "var(--yalola-text-muted)" }}>La saisie libre d'une obédience (« Autre ») est désactivée : les membres choisissent uniquement dans la liste ci-dessous. Ces propositions reçues avant la désactivation peuvent encore être approuvées (ajoutées à la liste) ou rejetées.</p>
            <div className="space-y-1">
              {pending.map((d) => (
                <div key={d.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl" style={{ background: "var(--yalola-primary-soft)" }} data-testid={`denom-pending-row-${d.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{d.name}</div>
                    <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>Proposée par {d.suggested_by_name || "un membre"}</div>
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-2 shrink-0">
                      <Button size="sm" className="pill-primary rounded-full h-8" onClick={() => approve(d)} data-testid={`denom-approve-${d.id}`}><Check size={14} className="mr-1" /> Approuver</Button>
                      <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => reject(d)} data-testid={`denom-reject-${d.id}`}><X size={14} className="mr-1" /> Rejeter</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      <div className="yalola-card p-4 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-bold font-display">Liste des obédiences</div>
          <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{items.filter((d) => d.status !== "pending").length} obédiences — utilisées à l'inscription, dans les profils et les fiches d'églises. L'ordre ci-dessous est l'ordre d'affichage.</p>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            <Input placeholder="Nouvelle obédience..." value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} className="w-52" data-testid="denom-new-name" />
            <Button className="pill-primary rounded-full" onClick={add} data-testid="denom-add-btn"><Plus size={14} className="mr-1" /> Ajouter</Button>
          </div>
        )}
      </div>

      <div className="yalola-card p-3">
        <div className="space-y-1">
          {items.filter((d) => d.status !== "pending").sort((a, b) => a.order - b.order).map((d, i) => (
            <div key={d.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors hover:bg-[color:var(--yalola-surface-alt)]" style={{ opacity: d.active === false ? 0.5 : 1 }} data-testid={`denom-row-${d.id}`}>
              <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: DOT_COLORS[i % DOT_COLORS.length] }} />
              <div className="flex-1 min-w-0">
                {editing?.id === d.id ? (
                  <div className="flex items-center gap-2">
                    <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} onKeyDown={(e) => e.key === "Enter" && saveRename()} className="h-8 max-w-[260px]" data-testid={`denom-edit-input-${d.id}`} autoFocus />
                    <button onClick={saveRename} title="Enregistrer" data-testid={`denom-edit-save-${d.id}`}><Check size={16} style={{ color: "var(--yalola-secondary)" }} /></button>
                    <button onClick={() => setEditing(null)} title="Annuler"><X size={16} style={{ color: "var(--yalola-text-muted)" }} /></button>
                  </div>
                ) : (
                  <>
                    <div className="text-sm font-semibold truncate">{d.name}</div>
                    <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>
                      {d.member_count || 0} membre{(d.member_count || 0) > 1 ? "s" : ""} · {d.church_count || 0} église{(d.church_count || 0) > 1 ? "s" : ""}
                    </div>
                  </>
                )}
              </div>
              {canManage && editing?.id !== d.id && (
                <div className="flex items-center gap-2 shrink-0">
                  <Switch checked={d.active !== false} onCheckedChange={(v) => patch(d.id, { active: v })} data-testid={`denom-active-${d.id}`} />
                  <div className="flex flex-col">
                    <button onClick={() => move(d, -1)} className="text-xs opacity-60 hover:opacity-100" data-testid={`denom-up-${d.id}`}>▲</button>
                    <button onClick={() => move(d, 1)} className="text-xs opacity-60 hover:opacity-100" data-testid={`denom-down-${d.id}`}>▼</button>
                  </div>
                  <button onClick={() => setEditing({ id: d.id, name: d.name })} title="Renommer" data-testid={`denom-edit-${d.id}`}><Pencil size={15} style={{ color: "var(--yalola-text-muted)" }} /></button>
                  <button onClick={() => removeD(d)} title="Supprimer" data-testid={`denom-del-${d.id}`}><Trash2 size={15} style={{ color: "#DC2626" }} /></button>
                </div>
              )}
            </div>
          ))}
          {items.filter((d) => d.status !== "pending").length === 0 && <div className="p-6 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }}>Aucune obédience.</div>}
        </div>
      </div>

      <div className="yalola-card p-5" data-testid="leader-titles-section">
        <div className="font-bold font-display">Titres des responsables d'église</div>
        <p className="text-xs mb-3" style={{ color: "var(--yalola-text-muted)" }}>Les 5 ministères (Éphésiens 4) et autres titres proposés dans les formulaires d'église.</p>
        <div className="flex flex-wrap gap-2">
          {(titles || []).map((t) => (
            <span key={t} className="chip flex items-center gap-1.5" data-testid={`leader-title-${t}`}>
              {t}
              {canManage && <button onClick={() => removeTitle(t)} title="Retirer" data-testid={`leader-title-del-${t}`}><X size={12} /></button>}
            </span>
          ))}
        </div>
        {canManage && (
          <div className="flex items-center gap-2 mt-3">
            <Input placeholder="Nouveau titre (ex : Berger)" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTitle()} className="max-w-[240px]" data-testid="leader-title-new" />
            <Button variant="outline" className="rounded-full" onClick={addTitle} data-testid="leader-title-add-btn"><Plus size={14} className="mr-1" /> Ajouter</Button>
          </div>
        )}
      </div>
    </div>
  );
};
